import axios from "axios";
import { getApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { toast } from "sonner";

import { tokens } from "./getToken";

export const axiosInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_BASE_URL,
  timeout: 0,
  headers: {
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache',
    'Expires': '0',
  },
});

// Helper function to safely decode JWT
const decodeJWT = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    throw new Error('Failed to decode token');
  }
};

// Request interceptor
axiosInstance.interceptors.request.use(
  async function (config) {
    if (typeof window !== "undefined") {
      const auth = getAuth(getApp());
      const localStorageUserId = localStorage?.getItem("connectedUserId");
      const localStorageReferralCode = localStorage?.getItem("referralCode");
      let idToken;
      let data;

      try {
        const user = auth.currentUser;
        if (user) {
          try {
            const currentToken = await user.getIdToken();
            const payload = decodeJWT(currentToken);
            const currentTime = Math.floor(Date.now() / 1000);

            if (payload.exp - currentTime < 900) {
              idToken = await user.getIdToken(true);
            } else {
              idToken = currentToken;
            }
          } catch (tokenError) {
            // If token decoding fails, force a refresh
            idToken = await user.getIdToken(true);
          }
        }

        if (!idToken) {
          data = await tokens();
        }
      } catch (error) {
        return Promise.reject(new Error("Authentication token retrieval failed"));
      }

      // Ensure headers object exists
      config.headers = config.headers || {};

      if (localStorageUserId) {
        config.headers["X-AryaCrypto-Masqueraded-User-Id"] = localStorageUserId;
      }
      if (localStorageReferralCode && (idToken || data?.token)) {
        config.headers["X-AryaCrypto-Referral-Code"] = localStorageReferralCode;
      }

      if (idToken) {
        config.headers.Authorization = `Bearer ${idToken}`;
      } else if (data?.token) {
        config.headers.Authorization = `Bearer ${data.token}`;
      }
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    if (
      typeof window !== "undefined" &&
      Object.hasOwn(response?.config?.headers, "X-AryaCrypto-Referral-Code")
    ) {
      const localStorageReferralCode = localStorage?.getItem("referralCode");
      if (localStorageReferralCode) {
        localStorage.removeItem("referralCode");
      }
    }
    return response;
  },
  function (error) {
    if (
      (error?.response?.data?.error ?? "") === "2FA required" ||
      (error?.response?.data?.error_code ?? 0) === 1064
    ) {
      if (!["/signin/2fa-confirmation", "/signin"].includes(window.location.pathname)) {
        window.location.replace("/signin/2fa-confirmation");
      }
    } else if (error?.response?.data?.error_code != null) {
      toast(error.response.data.message, {
        classNames: {
          content: "text-white",
        },
        style: {
          backgroundColor: "#ef4444",
          border: "0px"
        },
        duration: 5000
      });
    }

    return Promise.reject(error);
  }
);